import React from 'react';
import { AutoComplete, Button, Col, Form, Input, Row, Typography } from 'antd';
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import NodeSelect from '../NodeSelect';

const { Text } = Typography;

export function ListDynamicConfig({ config, onChange, varOptions = [] }) {
  const MAX_CUSTOM_ROWS = 8;
  const itemsVar = config.items_var ?? config.items_path ?? '';
  const customRows = Array.isArray(config.custom_rows) ? config.custom_rows : [];

  const updateCustomRow = (index, patch) => {
    const next = customRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange({ custom_rows: next });
  };

  const addCustomRow = () => {
    if (customRows.length >= MAX_CUSTOM_ROWS) return;
    onChange({
      custom_rows: [
        ...customRows,
        { id: '', label: '', target_node_id: '', capture_var: '' },
      ],
    });
  };

  const removeCustomRow = (index) => {
    onChange({ custom_rows: customRows.filter((_, i) => i !== index) });
  };

  return (
    <>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="List variable"
            extra="Session variable with the array (from API Save from API, e.g. divisions ← values)"
            required
          >
            <AutoComplete
              placeholder="divisions"
              value={itemsVar}
              onChange={(v) => onChange({ items_var: v || '', items_path: v || '' })}
              options={varOptions}
              filterOption={(input, option) =>
                option.value.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Value key" extra="Property on each item → WhatsApp list row id">
            <Input
              placeholder="value"
              value={config.value_field ?? config.id_field ?? 'value'}
              onChange={(e) => onChange({ value_field: e.target.value })}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Label key" extra="Property on each item → visible list title">
            <Input
              placeholder="label"
              value={config.label_field ?? config.title_field ?? 'label'}
              onChange={(e) => onChange({ label_field: e.target.value })}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label={`Custom rows (${customRows.length} / ${MAX_CUSTOM_ROWS})`}
        extra={
          customRows.length >= MAX_CUSTOM_ROWS
            ? '⚠️ Maximum 10 rows reached — Meta WhatsApp list limit.'
            : `Pinned on every page. ${MAX_CUSTOM_ROWS - customRows.length} slot${MAX_CUSTOM_ROWS - customRows.length === 1 ? '' : 's'} remaining.`
        }
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {customRows.map((row, i) => (
            <Col span={12} key={i}>
              <div
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  height: '100%',
                }}
              >
                <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={11}>
                    <Input
                      placeholder="Row id"
                      value={row.id ?? ''}
                      onChange={(e) => updateCustomRow(i, { id: e.target.value })}
                    />
                  </Col>
                  <Col span={11}>
                    <Input
                      placeholder="Label"
                      value={row.label ?? ''}
                      onChange={(e) => updateCustomRow(i, { label: e.target.value })}
                    />
                  </Col>
                  <Col span={2} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button type="text" danger onClick={() => removeCustomRow(i)} style={{ padding: 0 }}>
                      ×
                    </Button>
                  </Col>
                </Row>
                <Flex alignItems="center" gap={8} style={{ marginBottom: 8 }}>
                  <Text strong>{row.label || row.id || `Custom ${i + 1}`}</Text>
                  <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                  <Text type="secondary">Send this step</Text>
                </Flex>
                <NodeSelect
                  placeholder="Target step (optional)"
                  value={row.target_node_id}
                  onChange={(target_node_id) =>
                    updateCustomRow(i, { target_node_id: target_node_id || '' })
                  }
                  style={{ marginBottom: 8, width: '100%' }}
                />
                <AutoComplete
                  placeholder="Save pick as variable (optional)"
                  value={row.capture_var ?? ''}
                  onChange={(v) => updateCustomRow(i, { capture_var: v || '' })}
                  options={varOptions}
                  filterOption={(input, option) =>
                    option.value.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          ))}
        </Row>
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={addCustomRow}
          disabled={customRows.length >= MAX_CUSTOM_ROWS}
        >
          {customRows.length >= MAX_CUSTOM_ROWS
            ? `Limit reached (${MAX_CUSTOM_ROWS} / ${MAX_CUSTOM_ROWS})`
            : 'Add custom row'}
        </Button>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Header" extra="Max 60 characters">
            <Input
              value={config.header ?? ''}
              onChange={(e) => onChange({ header: e.target.value })}
              placeholder="DIVISIONS LIST"
              maxLength={60}
              showCount
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="List button text" extra="Max 20 characters">
            <Input
              value={config.button_text ?? 'Select'}
              onChange={(e) => onChange({ button_text: e.target.value })}
              placeholder="Select"
              maxLength={20}
              showCount
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Body" extra="Max 1024 characters (Meta limit)">
        <Input.TextArea
          value={config.body ?? ''}
          onChange={(e) => onChange({ body: e.target.value })}
          placeholder="Please choose an option"
          maxLength={1024}
          showCount
          autoSize={{ minRows: 2, maxRows: 5 }}
        />
      </Form.Item>

      <Form.Item label="Save selection as" extra="Variable for dynamic row picks (not custom rows)">
        <AutoComplete
          placeholder="selected_division"
          value={config.var_on_select ?? ''}
          onChange={(v) => onChange({ var_on_select: v || '' })}
          options={varOptions}
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
          allowClear
        />
      </Form.Item>

      <Form.Item
        label="On dynamic selection"
        extra="When customer picks a normal dynamic row, continue to this step"
      >
        <NodeSelect
          placeholder="Select next step (optional)"
          value={config.on_select_node_id}
          onChange={(on_select_node_id) => onChange({ on_select_node_id: on_select_node_id || '' })}
        />
      </Form.Item>
    </>
  );
}
